import { FC, useMemo, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { DefinitionTooltip } from "src/components/common";
import { getFacebookFollowersDefinitionInfo } from "../OrganicMediaDefinitions";
import { formatNumber } from "src/utils/numberFormat";

const MAX_TOP = 10;

const GEO_BAR_COLORS = [
  "#5ca6f8",
  "#31cdbc",
  "#a855f7",
  "#5ca6f8",
  "#16b981",
  "#f59e0b",
];

export interface FacebookFollowersGeoRow {
  name: string;
  followers: number;
  followersFormattedValue?: string;
  change_percent?: number;
}

interface RankedHoverState {
  clientX: number;
  clientY: number;
  row: FacebookFollowersGeoRow;
}

const CURSOR_TOOLTIP_OFFSET = 14;
const CURSOR_TOOLTIP_WIDTH = 260;
const CURSOR_TOOLTIP_HEIGHT = 110;
const VIEWPORT_MARGIN = 8;

const getCursorTooltipPosition = (clientX: number, clientY: number) => {
  const viewportWidth = typeof window === "undefined" ? 1024 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 768 : window.innerHeight;

  let viewportLeftEdge = 0;
  let viewportRightEdge = viewportWidth;
  const sidebarRect = typeof document === "undefined" ? undefined : document.querySelector(".sidebar")?.getBoundingClientRect();
  if (sidebarRect && sidebarRect.width > 0) {
    if (sidebarRect.left <= 1) {
      viewportLeftEdge = sidebarRect.right;
    } else if (sidebarRect.right >= viewportWidth - 1) {
      viewportRightEdge = sidebarRect.left;
    }
  }

  const minLeft = viewportLeftEdge + VIEWPORT_MARGIN;
  const maxLeft = Math.max(minLeft, viewportRightEdge - CURSOR_TOOLTIP_WIDTH - VIEWPORT_MARGIN);
  const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - CURSOR_TOOLTIP_HEIGHT - VIEWPORT_MARGIN);

  return {
    position: "fixed" as const,
    left: Math.max(minLeft, Math.min(clientX + CURSOR_TOOLTIP_OFFSET, maxLeft)),
    top: Math.min(clientY + CURSOR_TOOLTIP_OFFSET, maxTop),
    zIndex: 2000,
  };
};

function RankedRowTooltip({ clientX, clientY, row }: RankedHoverState) {
  const { t, i18n } = useTranslation();
  const hasChange =
    typeof row.change_percent === "number" && Number.isFinite(row.change_percent);

  return (
    <div
      className="organic-tooltip organic-tooltip--kpi organic-followers-ranked__tooltip"
      style={getCursorTooltipPosition(clientX, clientY)}
    >
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{row.name}</span>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.followers")}</span>
          <strong className="organic-tooltip__meta-value">
            {row.followersFormattedValue ?? formatNumber(row.followers, {}, i18n.language)}
          </strong>
        </div>
        {hasChange ? (
          <div className="organic-tooltip__meta-line">
            <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.vsPreviousPeriod")}</span>
            <strong className="organic-tooltip__meta-value">
              {`${(row.change_percent as number) > 0 ? "+" : ""}${formatNumber(row.change_percent as number, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}%`}
            </strong>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export interface FacebookRankedFollowersPanelProps {
  title: string;
  tooltipInfo: ReturnType<typeof getFacebookFollowersDefinitionInfo>;
  topLabel: string;
  rows: FacebookFollowersGeoRow[];
  value: number;
  disabled: boolean;
  onChange: (_value: number) => void;
  formulaTitle: string;
  emptyLabel: string;
}

const FacebookRankedFollowersPanel: FC<FacebookRankedFollowersPanelProps> = ({
  title,
  tooltipInfo,
  topLabel,
  rows,
  value,
  disabled,
  onChange,
  formulaTitle,
  emptyLabel,
}) => {
  const visibleRows = useMemo(
    () => rows.slice(0, value),
    [rows, value],
  );
  const maxFollowers =
    visibleRows.length > 0
      ? Math.max(...visibleRows.map(row => row.followers))
      : 1;
  const secondMaxFollowers = useMemo(() => {
    if (visibleRows.length < 2) {return maxFollowers;}
    const sorted = [...visibleRows].sort((a, b) => b.followers - a.followers);
    return sorted[1].followers || 1;
  }, [visibleRows, maxFollowers]);
  const hasOutlier = maxFollowers > secondMaxFollowers * 5;
  const [hover, setHover] = useState<RankedHoverState | null>(null);
  const { i18n } = useTranslation();

  return (
    <div className="organic-followers-ranked organic-content-panel">
      <div className="organic-followers-ranked__header">
        <div className="organic-content-panel__title">
          <h3>{title}</h3>
          <DefinitionTooltip
            label={title}
            info={tooltipInfo}
            formulaTitle={formulaTitle}
          />
        </div>
        <label className="organic-followers-ranked__control">
          <span>{topLabel}</span>
          <select
            value={value}
            disabled={disabled}
            onChange={event => onChange(Number(event.target.value) || 1)}
          >
            {Array.from({ length: MAX_TOP }, (_, index) => index + 1).map(option => (
              <option key={option} value={option}>
                {formatNumber(option, {}, i18n.language)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {visibleRows.length > 0 ? (
        <ul className="organic-followers-ranked__list">
          {visibleRows.map((row, index) => {
            let widthPercent: number;
            if (hasOutlier && row.followers === maxFollowers) {
              widthPercent = 100;
            } else if (hasOutlier) {
              widthPercent = secondMaxFollowers > 0
                ? (row.followers / secondMaxFollowers) * 80
                : 0;
            } else {
              widthPercent = maxFollowers > 0
                ? (row.followers / maxFollowers) * 100
                : 0;
            }
            const rowKey = `${row.name}-${row.followers}-${index}`;
            const handleMouseMove = (event: MouseEvent<HTMLLIElement>) => {
              setHover({ clientX: event.clientX, clientY: event.clientY, row });
            };

            return (
              <li
                key={rowKey}
                className="organic-followers-ranked__item"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHover(null)}
              >
                <span className="organic-followers-ranked__name">{row.name}</span>
                <span className="organic-followers-ranked__bar-track">
                  <span
                    className="organic-followers-ranked__bar-fill"
                    style={{
                      width: `${Math.max(4, widthPercent)}%`,
                      backgroundColor: GEO_BAR_COLORS[index % GEO_BAR_COLORS.length],
                    }}
                  />
                </span>
                <strong>{row.followersFormattedValue ?? formatNumber(row.followers, {}, i18n.language)}</strong>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="organic-followers-ranked__empty">{emptyLabel}</div>
      )}
      {hover && <RankedRowTooltip {...hover} />}
    </div>
  );
};

export default FacebookRankedFollowersPanel;
